import React, { memo, useCallback, useEffect, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { useAppContext } from "@app/providers/app"
import { useCart } from "@app/hooks/useCart"
import { FormButton } from "@app/components/Form/FormButton"
import { useOutOfStock } from "@app/hooks/useOutOfStock"
import { ProductCombinedProps } from "@root/types/global"

type Props = {
  handleErrors: () => void
  isLoading: boolean
  quantity: number
  product: ProductCombinedProps
  handleBackInStock: () => void
}

export const ProductFormAddToCart: React.FC<Props> = memo(({ handleErrors, handleBackInStock, isLoading, quantity, product }) => {
  const { product: productForm } = useStaticQuery<GatsbyTypes.ProductFormAddToCartQuery>(graphql`
    query ProductFormAddToCart {
      product: sanityTemplateProduct {
        additionalAddToCart
        additionalSoldOut
      }
    }
  `)
  const { activeVariant } = useAppContext()
  const { addToCart, loading } = useCart()

  const handleClick = useCallback(() => {
    activeVariant?.id ? addToCart(activeVariant.id, quantity, []) : !isLoading && handleErrors()
  }, [activeVariant, isLoading, handleErrors, quantity, addToCart])

  const isSoldOut =
    useMemo(
      () =>
        (activeVariant && !activeVariant?.quantityAvailable && !isLoading) ||
        product.variants.every(variant => variant.availableForSale === false || variant.quantityAvailable === 0),
      [activeVariant, isLoading, product.variants]
    ) || false

  const { getStockStatus } = useOutOfStock()

  const { stockStatus, isNotifyFlow, isInStoreOnly } = getStockStatus(product, isSoldOut)
  const [label, setLabel] = React.useState<string>(
    stockStatus ? stockStatus : isSoldOut ? productForm?.additionalSoldOut : productForm?.additionalAddToCart
  )

  useEffect(() => {
    const { stockStatus } = getStockStatus(product, isSoldOut)
    setLabel(stockStatus ? stockStatus : isSoldOut ? productForm?.additionalSoldOut : productForm?.additionalAddToCart)
  }, [getStockStatus, isSoldOut, product, productForm?.additionalAddToCart, productForm?.additionalSoldOut])

  return (
    <FormButton
      isDisabled={(!isNotifyFlow && isSoldOut) || isInStoreOnly || isLoading}
      isLoading={loading}
      onClick={isNotifyFlow ? handleBackInStock : handleClick}
      size="md"
      title={label}
      aria-label={label}
      variant="pdpAddToCart"
      w="full"
    >
      {label}
    </FormButton>
  )
})
